/**
 * `fetch`, plus the scope's firm claim (spec customer-success/v1.5.1/002 §2.1;
 * multi-product 022 G48b).
 *
 * NO SCOPE HEADER (WP3.17.2a-b2). The server takes a request's scope from the
 * acting firm and ignores the retired scope header (U19), so the client
 * stopped sending it; `src/__tests__/boundaries/identity-claims-published.test.ts` keeps it gone.
 * What the per-tree scope still selects is the `x-cova-org` claim below: which
 * of the person's firms this tab acts as for that scope.
 *
 * A DELEGATE, NEVER A PATCH. Assigning to `globalThis.fetch` was refused: the
 * same module graph is evaluated on the server, where a patched global would
 * tag Clerk's calls, the RPC server's own outbound requests and every
 * third-party SDK with a header they never asked for. This function is handed
 * to the handful of raw `fetch` call sites; `AppHttpClient` adds the same
 * headers (`scopeRequestHeaders`) through its own request mapping.
 *
 * `globalThis.fetch` is read AT CALL TIME, not captured — which is what keeps
 * the global MFA gate intact. `installMfaFetchInterceptor` replaces
 * `globalThis.fetch` when the query layer loads, and a captured reference here
 * would route every scoped request around it, turning a 401 step-up into an
 * opaque RPC defect on exactly the surfaces this file is for.
 *
 * No claim ⇒ no `Headers` object materialised: the request goes out exactly as
 * the caller built it.
 *
 * THE FIRM CLAIM (spec multi-product 005 §4 "Platform web",
 * 007 §2.1; board unit WP1.24.8). `x-cova-org` carries the firm this tab has
 * active for the request's scope (`@/shared/active-firm`), read per request.
 * No active firm ⇒ no header, so a tab that has not resolved identity yet
 * sends exactly what it sent before.
 */
import { DEFAULT_PLATFORM_SCOPE, type PlatformScope } from "@/domain/scope/platform-scope"
import { activeFirmHeaders } from "@/shared/active-firm/active-firm-headers"
import { Effect, Either, Fiber, FiberRef, Option } from "effect"
import { globalValue } from "effect/GlobalValue"
import { getViewAs, isViewerQueryClient, putViewAsHeaders, type ViewAs } from "./view-as-store"
import {
  STAFF_VIEW_RPC_PREFIXES,
  STAFF_VIEW_RPC_READS,
  type StaffViewRpcGroup,
} from "@/contexts/bms/staff/viewer/domain"

/**
 * The headers a request for `scope` carries, read per call: `x-cova-org` when
 * this tab has a firm for the scope, nothing otherwise. Spelled once so the raw
 * `fetch` path and `AppHttpClient` agree.
 */
export const scopeRequestHeaders = (scope: PlatformScope): Readonly<Record<string, string>> =>
  activeFirmHeaders(scope)()

/**
 * `fetch` tagged with an EXPLICIT scope, for a caller that already knows which
 * product its request belongs to and must not be at the mercy of which tree
 * called it (R-1).
 *
 * The identity read is the one such caller: its cache key IS a scope, and a
 * request keyed `insurance-broker` that went out carrying `customer` would file the Customer
 * identity under the broker's key — a person's organisation, silently swapped in
 * the cache. Everything else in the app is a per-surface query whose scope is
 * whatever the surface is: `AppHttpClient` reads it per request off the
 * running fiber (`withRequestScope`).
 */
export const fetchWithScope =
  (scope: PlatformScope): typeof fetch =>
  (input, init) => {
    const ours = Object.entries(scopeRequestHeaders(scope))
    if (ours.length === 0) return globalThis.fetch(input, init)

    // `Headers` merges every input shape (a `Headers`, a record, a tuple list)
    // and the caller's own value for the key would win — nobody sets it, but a
    // silent override is not a thing to leave to chance, so ours are set LAST.
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    )
    for (const [name, value] of ours) headers.set(name, value)
    return globalThis.fetch(input, { ...init, headers })
  }

// ============================================================================
// The CRM viewer's view (spec S1 and S6, research 08 §6.3, notes 01 B-06)
// ============================================================================

/**
 * The BMS staff groups. Each answers about a firm the staff person names in its
 * PATH, under their own session, so the view's headers are never added to it:
 * the session middleware would read them as a claim it does not admit there.
 * Exactly the prefixes of the groups behind `BmsSuperAdminMiddleware`, both
 * ways (`bms-view-as-delegate-lists.test.ts`, D-03); a new staff group joins.
 */
export const VIEW_AS_EXCLUDED_PREFIXES = [
  "/api/bms/sync-admin-views",
  "/api/bms/cutover-firms-admin-views",
  "/api/bms/cutover-firms-admin-actions",
  "/api/bms/cutover-matcher-admin-views",
  "/api/bms/crm-viewer-admin-views",
  "/api/bms/sync-admin-actions",
  // The Cova CRM page's two staff groups (decision 033).
  "/api/bms/firm-switches-admin-views",
  "/api/bms/firm-switches-admin-actions",
  // The staff remap of a wrong join's documents (decision 043 BLOCKER 4).
  "/api/bms/cutover-remap-admin-actions",
  // The staff dry run of a firm's move (spec bms 2026-11-01-001).
  "/api/bms/cutover-dry-run-admin-actions",
  // Staff onboarding a firm on its written instruction (decision 059).
  "/api/bms/cutover-onboarding-staff-actions",
  // Staff answering a firm's shared-site and tenancy questions (spec 2026-11-04-001).
  "/api/bms/sync-tenancy-staff",
] as const

/**
 * The RPC groups the firm's screens can reach that are CUT while a view is set
 * (spec S11, research 08 §6.3). These groups have no view door: they resolve
 * the organisation from the session, so under the viewer they would answer
 * for the staff person's own firm, or write to it. Each is answered here with
 * a 403 and nothing is sent (and the server's identity door refuses the claim
 * on them too). D-03 checks every RPC URL the BMS slice names and every one
 * its imports reach in the team, meetings and clients slices. The meetings
 * group is the one reached from outside the slice: the firm's Tasks screen
 * reads the signed-in person's meetings, which under the viewer would be the
 * staff person's own; the calendar that shows them is not mounted.
 *
 * THE SEVEN FILES AND FORMS GROUPS LEFT THIS LIST (brief H): they carry the
 * view door now, and their prefixes are `STAFF_VIEW_RPC_PREFIXES`, judged
 * per tag below. `/api/rpc/form` stays, and so `ClientFormsRpc` and
 * `FormSendRpc`, which have no read the viewer needs, stay refused; the two
 * allowlisted form groups beneath it are matched first.
 */
export const VIEW_AS_REFUSED_PREFIXES = [
  "/api/rpc/form",
  "/api/rpc/clients-access-user",
  "/api/rpc/teams-admin",
  "/api/rpc/audit/version-control",
  "/api/rpc/bms/insurer-registry/org-insurers",
  "/api/rpc/bms/abn-lookup",
  "/api/rpc/meetings",
] as const

/** The firm's own BMS lane, where the headers go. */
const BMS_PREFIX = "/api/bms"

/** A prefix match on a SEGMENT boundary: `/api/rpc/form` takes `/api/rpc/form/send`, never `/api/rpc/formulas`. */
export const matchesPrefix = (path: string, prefix: string): boolean =>
  path === prefix || path.startsWith(`${prefix}/`)

/**
 * The request's path, or null when it is not this app's: a relative URL is the
 * app's, an absolute one is the app's only on this origin, so a third-party
 * URL that happens to contain `/api/bms/` never carries the view.
 */
const sameOriginPathOf = (input: RequestInfo | URL): string | null => {
  const raw = input instanceof Request ? input.url : input instanceof URL ? input.href : input
  const origin = typeof window === "undefined" ? "http://localhost" : window.location.origin
  const url = new URL(raw, origin)
  // Origin only: a protocol-relative `//other.host/...` starts with "/" too,
  // and resolves to another host, so a leading slash proves nothing.
  return url.origin === origin ? url.pathname : null
}

/**
 * WHICH CLIENT ASKED (rung 3, fix 3 finding 1). The view is one module value
 * for the whole document, so on its own it would tag every `/api/bms/` read the
 * page makes, the app's own included: the root layout's cutover banner reads
 * the staff person's own firm through the app's `QueryClient`, and with the
 * view on it the server answered 403 for their firm and 404 for any other.
 *
 * So a query knows which `QueryClient` ran it. The query glue
 * (`http-api-query.ts`, `effect-query-utils.ts`) passes TanStack's
 * `context.client` to `laneForQueryClient`, and a client the viewer did not
 * register (`markViewerQueryClient`) runs its effect with this flag set.
 * `scopedFetch` reads the flag off the fiber that calls it: `FetchHttpClient`
 * calls `fetch` inside the requesting fiber, and a fiber forked under it (the
 * timeout's race) inherits the flag.
 *
 * FAIL CLOSED. Only a request positively from ANOTHER client loses the view.
 * A request with no client at all (an imperative `mutationFn(undefined)`, a
 * raw call, an RPC protocol fiber) keeps the page's view, as before, so a firm
 * screen's read is never sent as the staff person's own.
 */
const outsideTheView = globalValue(Symbol.for("@cova/shared/scope/outsideTheView"), () => FiberRef.unsafeMake(false))

/** True when the fiber making this request was started for a client other than the viewer's. */
const madeOutsideTheView = (): boolean =>
  Option.match(Fiber.getCurrentFiber(), { onNone: () => false, onSome: (fiber) => fiber.getFiberRef(outsideTheView) })

/**
 * The lane a query's effect runs in, from the `QueryClient` that ran it: the
 * viewer's own client, or none named, keeps the view; any other client never
 * carries it. Wraps the WHOLE run, after its layer is provided.
 */
export const laneForQueryClient =
  (client: unknown) =>
  <A, E, R>(effect: Effect.Effect<A, E, R>): Effect.Effect<A, E, R> =>
    client === undefined || client === null || isViewerQueryClient(client)
      ? effect
      : Effect.locally(effect, outsideTheView, true)

/** The allowlisted group an RPC path mounts, or undefined. */
const staffViewGroupOf = (path: string): StaffViewRpcGroup | undefined => {
  for (const [prefix, group] of Object.entries(STAFF_VIEW_RPC_PREFIXES)) {
    if (matchesPrefix(path, prefix)) return group
  }
  return undefined
}

/**
 * The RPC tags a request's body names, or null when the body cannot be read
 * here and now: a `Request` input (its body is a stream), no body, or a body
 * that is not the JSON protocol's message or an array of them. The JSON
 * protocol sends one `{ _tag: "Request", tag, ... }` per POST
 * (`RpcClient.makeProtocolHttp`); anything else is not a read this delegate
 * can vouch for.
 */
const rpcTagsOf = (input: RequestInfo | URL, init: RequestInit | undefined): ReadonlyArray<string> | null => {
  if (input instanceof Request) return null
  const body = init?.body
  const text =
    typeof body === "string"
      ? body
      : body instanceof Uint8Array || body instanceof ArrayBuffer
        ? new TextDecoder().decode(body)
        : null
  if (text === null) return null
  const parsed = Either.try((): unknown => JSON.parse(text))
  if (Either.isLeft(parsed)) return null
  const messages: ReadonlyArray<unknown> = Array.isArray(parsed.right) ? parsed.right : [parsed.right]
  const tags: Array<string> = []
  for (const message of messages) {
    if (typeof message !== "object" || message === null) return null
    const { _tag, tag } = message as { readonly _tag?: unknown; readonly tag?: unknown }
    if (_tag !== "Request" || typeof tag !== "string") return null
    tags.push(tag)
  }
  return tags.length === 0 ? null : tags
}

/**
 * Whether a POST to an allowlisted group may leave with the view: every tag
 * in its body is one of that group's reads (brief H ruling 8). Every RPC call
 * is a POST, so the BMS lane's "only a GET leaves" cannot tell a read from a
 * write here; the tag can.
 */
const isAllowlistedRead = (group: StaffViewRpcGroup, input: RequestInfo | URL, init: RequestInit | undefined): boolean => {
  const tags = rpcTagsOf(input, init)
  const reads: ReadonlySet<string> = STAFF_VIEW_RPC_READS[group]
  return tags !== null && tags.every((tag) => reads.has(tag))
}

/** A request's method, upper case; a bare URL or a `Request` built without one is a GET. */
const methodOf = (input: RequestInfo | URL, init: RequestInit | undefined): string =>
  (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase()

/** What a cut RPC call answers: the session middleware's own refusal shape, so a screen reads it as one. */
const refusedResponse = (): Response =>
  new Response(
    JSON.stringify({ code: "insufficient_scope", message: "The CRM viewer is read-only, and this part of the screen is not shown in it." }),
    { status: 403, headers: { "content-type": "application/json" } },
  )

/**
 * What a refused write on the firm's BMS lane answers: the BMS public error
 * envelope the server's own 403 carries, so the firm's HttpApi clients decode
 * it as `BmsApiForbiddenError` exactly as they would the server's. The trace
 * id is minted here and says so: no request left, so no server trace exists.
 */
const LOCAL_REFUSAL_TRACE_ID = "local-crm-viewer-read-only"

const refusedBmsWrite = (): Response =>
  new Response(
    JSON.stringify({
      error: {
        code: "insufficient_scope",
        message: "The CRM viewer is read-only. Nothing on this page changes the firm.",
        traceId: LOCAL_REFUSAL_TRACE_ID,
      },
    }),
    { status: 403, headers: { "content-type": "application/json" } },
  )

const withView = (input: RequestInfo | URL, init: RequestInit | undefined, view: ViewAs): RequestInit => {
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
  return { ...init, headers: putViewAsHeaders(headers, view) }
}

/**
 * `fetch` for a caller that names NO scope: the default (`insurance-broker`)
 * claim, never a section's (FIX-SCOPE-STORE), and while the CRM viewer is
 * open, its view.
 *
 * It used to read a module-level "current scope" that the section provider
 * wrote, so a root-layout widget polling while `/cs` was open sent the
 * section's claim and cached the answer under its scope-free key (the flicker,
 * `__tests__/scope-store-flicker.repro.test.tsx`). A plain function has no way
 * to know which React tree called it, so a caller that renders under a section
 * passes the tree's scope instead: `fetchWithScope(usePlatformScope())`.
 *
 * WITH NO VIEW SET this is exactly `fetchWithScope(DEFAULT_PLATFORM_SCOPE)`: a
 * broker request is byte-identical to what it was (S11). With a view set:
 *
 * - the cut RPC groups answer 403 without a request;
 * - the seven files and forms groups with a view door (brief H): a call whose
 *   every tag is one of its group's allowlisted reads leaves with the view's
 *   headers; any other call, a write or a body this cannot read, answers 403
 *   without a request, the same refusal the server's door gives;
 * - EVERY WRITE on the firm's BMS lane answers a 403 in the BMS error
 *   envelope without a request (plan §8: refused on the browser and the
 *   server). Only a GET leaves. The
 *   firm's screens send their preference PUTs on mount, imperatively, past the
 *   viewer's `MutationCache`, so the refusal is here, page wide;
 * - a READ on the firm's BMS lane gains the two view-as headers, unless the
 *   query that sent it was run by a client other than the viewer's (the app's
 *   own banner, `laneForQueryClient`);
 * - a staff group and everything off `/api/bms/` stay untouched.
 *
 * Every app HTTP client goes through this delegate (`withViewAs` below), the
 * scope-pinned `AppHttpClient.layerForScope` included, so the identity read
 * and the root providers keyed by scope are view-free because their paths are
 * off `/api/bms/` and off every view and refused group, not by being exempt.
 */
export const scopedFetch: typeof fetch = (input, init) => withViewAs(fetchWithScope(DEFAULT_PLATFORM_SCOPE))(input, init)

/**
 * The CRM viewer's delegate over `base`, the rules `scopedFetch` lists above.
 * With no view set it is `base`. `AppHttpClient` hands it to `FetchHttpClient`
 * over a bare `fetch`, because its own request mapping already carries the
 * scope's claim for the fiber's scope; layering `scopedFetch` there would set
 * the default claim over a section's. Every app HTTP client takes the view
 * through it, so the view cannot ride a request one client sends and another
 * does not (FIX-SCOPE-STORE dropped it from `AppHttpClient`, L1 G5).
 */
export const withViewAs =
  (base: typeof fetch): typeof fetch =>
  (input, init) => {
    const view = getViewAs()
    if (view === null) return base(input, init)
    const path = sameOriginPathOf(input)
    if (path === null) return base(input, init)
    const group = staffViewGroupOf(path)
    if (group !== undefined) {
      if (!isAllowlistedRead(group, input, init)) return Promise.resolve(refusedResponse())
      if (madeOutsideTheView()) return base(input, init)
      return base(input, withView(input, init, view))
    }
    if (VIEW_AS_REFUSED_PREFIXES.some((prefix) => matchesPrefix(path, prefix))) return Promise.resolve(refusedResponse())
    if (!matchesPrefix(path, BMS_PREFIX) || VIEW_AS_EXCLUDED_PREFIXES.some((prefix) => matchesPrefix(path, prefix))) {
      return base(input, init)
    }
    if (methodOf(input, init) !== "GET") return Promise.resolve(refusedBmsWrite())
    if (madeOutsideTheView()) return base(input, init)
    return base(input, withView(input, init, view))
  }
